import {SHOW_HIDE_FIELDS} from 'lib/ShowHideFields';
import {getIn} from 'lib/immutableObject';
import {getComparableKey} from 'lib/Commons';
import {castArray, isEmpty} from 'lodash/lang';
import { round } from 'lodash';
import RapidSearch from 'constants/rapidSearch';

export const comparableTypes = ['sales', 'listings', 'rentals'];
export const reportTypes = ['cma', 'rental', 'soi'];

/** For backwards compatibility with the V1 templates, the API prefers listingPrice field values,
 *  but once the price description flags are removed we should be able to move to latestPublishedPrice.
 *  Moving latestPublishedPrice to the first index should update the preference for the rest of the app.
 * @type {string[]}
 */
export const comparableListingUpdateFields = ['salePrice', 'latestPublishedPrice'];
export const oldListingPriceMap = {
  firstAdvertisementPrice: 'firstPublishedPrice',
  latestAdvertisementPrice: 'latestPublishedPrice',
};
export const numberFields = ['salePrice', 'firstAdvertisementPrice', 'latestAdvertisementPrice'];
export const MASK_VALUE = '-';
export const MASK_VALUE_NZ = 'Not Disclosed';

export const isNZLocale = () => window.location.hostname.includes('nz');
export const getIsFieldVisible = (field, displayFields = {}) => displayFields[field] !== false;
export const getCountryCode = () => (isNZLocale() ? 'nz' : 'au');
export const getDefaultDescription = () => (isNZLocale() ? MASK_VALUE_NZ : MASK_VALUE);
export const getSaleAvm = (propertyDetail) => propertyDetail.intelliValAvm;

export const getCompItemTitlePrice = (comparableType, property) => {
  const priceFieldKey = 'salePrice';
  const { rentalLastCampaignLastListedPeriod, displayFields } = property;
  if (!getIsFieldVisible(priceFieldKey, displayFields)) {
    return getDefaultDescription();
  }
  const value = property[priceFieldKey];
  if (comparableType === RapidSearch.COMPARABLE_TYPE.RENTALS) {
    return value && value !== MASK_VALUE
        ? `$${value}${rentalLastCampaignLastListedPeriod ? `/${rentalLastCampaignLastListedPeriod}` : ''}`
        : MASK_VALUE;
  }
  return value?.toString();
};

export const getPropertyFromState = (propertyId, comparableType, state, isComparable) => {
  // adding isComparable to the function signature for backwards compatibility
  // isComparable is used to determine if the propertyId is from the main property detail or a comparable
  // however, added comparable can be equivalent to the main/target property detail
  const _isComparable = isComparable || propertyId !== state.propertyDetail?.propertyId;
  if (_isComparable) {
    if (!comparableType) {
      return {};
    }
    const comparableKey = getComparableKey(comparableType);
    return getIn(state, [comparableKey, 'properties', propertyId]);
  }
  return state.propertyDetail;
};

export const getFetchingFromState = (
  {
    propertyId,
    comparableType,
    state,
    updateFields,
  }
) => {
  if (!propertyId) {
    return false;
  }
  const property = getPropertyFromState(propertyId, comparableType, state);
  const fetching = property?.fetching ?? {};

  const fields = castArray(updateFields);
  return !isEmpty(fetching) && Object.keys(fetching).some(f => fetching[f] && fields.includes(f));
};

/**
 * Report request alterations to change a comparable item's displayed field styling.
 * @param originalRequest an object representing a comparable property
 * @param displayFields an object containing the fields to hide as a key
 * @returns {*} request with the actual values changed to hide them
 */
export const maskRequest = (originalRequest, displayFields = {}) => {
  const defaultValue = getDefaultDescription();
  // Ensure that checked fields have at least a default value
  for (let f of SHOW_HIDE_FIELDS) {
    originalRequest[f] = originalRequest[f] || defaultValue;
  }
  const maskedRequest = Object.assign({}, originalRequest);
  for (let key of Object.keys(displayFields)) {
    const value = originalRequest[key];
    if (getIsFieldVisible(key, displayFields)) {
      maskedRequest[key] = value;
    } else {
      // Prevent the API from rejecting a value in a different format
      if (typeof value === 'string') {
        maskedRequest[key] = defaultValue;
      } else {
        delete maskedRequest[key];
      }
      // Hide the disclaimer mark if a value is hidden
      const editedKey = `${key}IsEdited`;
      maskedRequest[editedKey] = false;
    }
  }
  return maskedRequest;
};

/**
 * Backwards compatibility utility to remove the price description fields from a given property object
 *
 * @param request Property object with the new/last listing prices
 */
export const removeNewPriceDescriptionFields = (request) => {
  Object.values(oldListingPriceMap).forEach((key) => delete request[key]);
};

export const isVIC = (address) => address && address.includes(' VIC ') && !isNZLocale();

const getDistance = ({ lat: lat1, lon: lon1 }, { lat: lat2, lon: lon2 }) => {
  const toRadians = (deg) => (deg * Math.PI) / 180;

  const R = 6371; // Radius of the Earth in km
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return round(R * c, 2); // Distance in km
};

export const getPropertyDistanceFromLocation = (targetProperty, originalLocation) => {
  if (targetProperty.lat
      && targetProperty.lon
      && originalLocation.lat
      && originalLocation.lon) {
    return getDistance(targetProperty, originalLocation);
  }

  return null;
};
